import React, { useState, useCallback } from "react"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  alpha,
} from "@mui/material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import StopIcon from "@mui/icons-material/Stop"
import AdsClickIcon from "@mui/icons-material/AdsClick"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import useCameraStream from "../../hooks/useCameraStream"

const ProjectCamera = () => {
  const [homography, setHomography] = useState(null)
  const [error, setError] = useState(null)

  const fetchHomography = useCallback(
    async (file, { setLoading, loadingRef }) => {
      setLoading(true)
      loadingRef.current = true
      const formData = new FormData()
      formData.append("image", file)

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || ""}/api/find_homography`,
          {
            method: "POST",
            body: formData,
          }
        )
        const json = await response.json()
        if (json.error) {
          setError(json.error)
          setHomography(null)
        } else {
          setHomography(json.homography)
          setError(null)
        }
      } catch (err) {
        console.error(err)
        setError(err.message)
      } finally {
        setLoading(false)
        loadingRef.current = false
      }
    },
    []
  )

  const {
    videoRef,
    canvasRef,
    devices,
    selectedDeviceId,
    handleDeviceChange,
    isCapturing,
    toggleCapture,
    loading,
  } = useCameraStream({ onFile: fetchHomography })

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Controls bar */}
      <Card sx={{ width: "100%", maxWidth: 640 }}>
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            "&:last-child": { pb: 2 },
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Camera</InputLabel>
            <Select
              value={selectedDeviceId}
              onChange={handleDeviceChange}
              label="Camera"
            >
              {devices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(0, 8)}...`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant={isCapturing ? "outlined" : "contained"}
            onClick={toggleCapture}
            startIcon={isCapturing ? <StopIcon /> : <PlayArrowIcon />}
            color={isCapturing ? "error" : "primary"}
            sx={{ minWidth: 120 }}
          >
            {isCapturing ? "Stop" : "Start"}
          </Button>

          {isCapturing && (
            <FiberManualRecordIcon
              sx={(theme) => ({
                color: theme.palette.error.main,
                fontSize: 14,
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              })}
            />
          )}
        </CardContent>
      </Card>

      {/* Camera feed */}
      <Card sx={{ width: "100%", maxWidth: 640, overflow: "hidden" }}>
        <Box
          component="video"
          ref={videoRef}
          autoPlay
          playsInline
          muted
          sx={{
            width: "100%",
            display: "block",
            bgcolor: "background.default",
            // Reserve space before video loads so the card doesn't collapse
            minHeight: 200,
          }}
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </Card>

      {/* Status indicators */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            color: "text.secondary",
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">Computing homography...</Typography>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ width: "100%", maxWidth: 640 }}
        >
          {error}
        </Alert>
      )}

      {/* Homography output */}
      <Card sx={{ width: "100%", maxWidth: 640 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography
            variant="h6"
            sx={{ mb: 1.5, fontSize: "0.85rem", color: "text.secondary" }}
          >
            Homography Matrix
          </Typography>
          {homography ? (
            <TextField
              multiline
              fullWidth
              rows={6}
              value={JSON.stringify(homography, null, 2)}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", fontSize: "0.85rem" },
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                py: 3,
                color: "text.secondary",
              }}
            >
              <AdsClickIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              <Typography variant="body2" sx={{ opacity: 0.6 }}>
                Start the camera to compute a homography matrix
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default ProjectCamera
